import React from 'react';
import { Truck } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PortOption } from '../services/commonApi';
import { TransporterMaster, getTransporterDisplayName } from '../services/transporterMasterApi';

export interface InvoiceTransportFormValues {
  ewayTransportMode: string;
  motorVehicleNo: string;
  transporterMasterId: string;
  ewayTransporterId: string;
  ewayTransporterName: string;
  ewayDistanceKm: string;
  portId: string;
  manualPincode: string;
}

export function validateInvoiceTransportDetails(
  values: InvoiceTransportFormValues,
  exportSupply: boolean
): string | null {
  if (exportSupply) {
    const portIdNum = parseInt(values.portId, 10);
    if (!values.portId.trim() || Number.isNaN(portIdNum) || portIdNum <= 0) {
      return 'Please select a port.';
    }
    if (!/^\d{6}$/.test(values.manualPincode.trim())) {
      return 'Please enter a valid 6-digit manual pincode.';
    }
  }
  return null;
}

interface InvoiceTransportDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceLabel?: string;
  exportSupply: boolean;
  value: InvoiceTransportFormValues;
  onChange: (value: InvoiceTransportFormValues) => void;
  transporters: TransporterMaster[];
  loadingTransporters?: boolean;
  ports: PortOption[];
  loadingPorts?: boolean;
  /** When set, transport mode uses dropdown; otherwise free-text input */
  transportModes?: string[];
  /** View page: read-only until Edit; create/edit form: always editable */
  allowViewMode?: boolean;
  editable?: boolean;
  saving?: boolean;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  title?: string;
  description?: string;
}

export const InvoiceTransportDetailsDialog: React.FC<InvoiceTransportDetailsDialogProps> = ({
  open,
  onOpenChange,
  invoiceLabel,
  exportSupply,
  value,
  onChange,
  transporters,
  loadingTransporters = false,
  ports,
  loadingPorts = false,
  transportModes,
  allowViewMode = false,
  editable = true,
  saving = false,
  onConfirm,
  confirmLabel = 'Save',
  title = 'Transport details for e-way bill',
  description = 'Enter transport details for this invoice. They are saved on the invoice header only.',
}) => {
  const [viewMode, setViewMode] = React.useState(true);
  const isEditable = allowViewMode ? !viewMode : editable;

  React.useEffect(() => {
    if (open && allowViewMode) setViewMode(true);
  }, [open, allowViewMode]);

  const handleTransporterSelect = (masterId: string) => {
    if (!masterId || masterId === '__none__') {
      onChange({
        ...value,
        transporterMasterId: '',
        ewayTransporterName: '',
        ewayTransporterId: '',
      });
      return;
    }
    const selected = transporters.find((t) => String(t.id) === masterId);
    onChange({
      ...value,
      transporterMasterId: masterId,
      ewayTransporterName: selected?.transporterName?.trim() ?? '',
      ewayTransporterId: selected?.transporterId?.trim() ?? '',
    });
  };

  const patch = (partial: Partial<InvoiceTransportFormValues>) => onChange({ ...value, ...partial });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (saving) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {(invoiceLabel || allowViewMode) && (
          <div className="flex items-center justify-between gap-3">
            {invoiceLabel ? (
              <div className="text-sm text-muted-foreground">
                Invoice: <span className="font-medium text-foreground">{invoiceLabel}</span>
              </div>
            ) : (
              <div />
            )}
            {allowViewMode ? (
              <Button type="button" variant="outline" onClick={() => setViewMode((v) => !v)}>
                {viewMode ? 'Edit' : 'View mode'}
              </Button>
            ) : null}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="transportMode">Transport mode</Label>
            {transportModes && transportModes.length > 0 ? (
              <Select
                value={value.ewayTransportMode || '__none__'}
                onValueChange={(v) => patch({ ewayTransportMode: v === '__none__' ? '' : v })}
                disabled={!isEditable}
              >
                <SelectTrigger id="transportMode" className="border border-gray-300 rounded-xl">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {transportModes.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {mode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="transportMode"
                value={value.ewayTransportMode}
                onChange={(e) => patch({ ewayTransportMode: e.target.value })}
                disabled={!isEditable}
                placeholder="Road / Air / Rail / Ship"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="transportVehicleNo">Vehicle no.</Label>
            <Input
              id="transportVehicleNo"
              value={value.motorVehicleNo}
              onChange={(e) => patch({ motorVehicleNo: e.target.value })}
              disabled={!isEditable}
              placeholder="e.g. MH12AB1234"
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label>Transporter</Label>
            {isEditable ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 min-w-0">
                  <Label htmlFor="transportTransporterMaster" className="text-xs text-muted-foreground font-normal">
                    Name
                  </Label>
                  <Select
                    value={value.transporterMasterId || '__none__'}
                    onValueChange={handleTransporterSelect}
                    disabled={loadingTransporters}
                  >
                    <SelectTrigger
                      id="transportTransporterMaster"
                      className="border border-gray-300 rounded-xl min-w-0 w-full [&>span]:block [&>span]:truncate [&>span]:text-left"
                    >
                      <SelectValue
                        placeholder={loadingTransporters ? 'Loading transporters…' : 'Select transporter'}
                      />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search transporter..." debounceMs={200}>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {[...transporters]
                        .sort((a, b) =>
                          getTransporterDisplayName(a).localeCompare(getTransporterDisplayName(b), undefined, {
                            sensitivity: 'base',
                          })
                        )
                        .map((t) => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {getTransporterDisplayName(t)}
                            {t.transporterId ? ` (${t.transporterId})` : ''}
                          </SelectItem>
                        ))}
                    </SelectContentWithSearch>
                  </Select>
                </div>
                <div className="space-y-1.5 min-w-0">
                  <Label htmlFor="transportTransporterId" className="text-xs text-muted-foreground font-normal">
                    ID
                  </Label>
                  <Input
                    id="transportTransporterId"
                    value={value.ewayTransporterId}
                    readOnly
                    disabled
                    placeholder="Auto-filled from transporter"
                    className="bg-muted/40"
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground py-2 px-3 rounded-md border border-gray-200 bg-muted/30 break-words">
                <span className="font-medium">{value.ewayTransporterName || '—'}</span>
                {value.ewayTransporterId ? (
                  <span className="text-muted-foreground">
                    {' '}
                    · <span className="font-normal">ID:</span> {value.ewayTransporterId}
                  </span>
                ) : null}
              </p>
            )}
          </div>

          {exportSupply ? (
            <>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="transportPortId">Port *</Label>
                <Select
                  value={value.portId || undefined}
                  onValueChange={(v) => patch({ portId: v })}
                  disabled={!isEditable || loadingPorts}
                >
                  <SelectTrigger id="transportPortId" className="border border-gray-300 rounded-xl w-full">
                    <SelectValue placeholder={loadingPorts ? 'Loading ports…' : 'Select port'} />
                  </SelectTrigger>
                  <SelectContentWithSearch
                    searchPlaceholder="Search port..."
                    className="min-w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,32rem)]"
                  >
                    {ports.map((p) => (
                      <SelectItem key={p.value} value={p.value} textValue={p.label}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="transportDistanceKm">Distance (KM)</Label>
                  <Input
                    id="transportDistanceKm"
                    value={value.ewayDistanceKm}
                    onChange={(e) => patch({ ewayDistanceKm: e.target.value })}
                    disabled={!isEditable}
                    inputMode="numeric"
                    placeholder="e.g. 120"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transportManualPincode">Manual pincode *</Label>
                  <Input
                    id="transportManualPincode"
                    value={value.manualPincode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                      patch({ manualPincode: v });
                    }}
                    disabled={!isEditable}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="6-digit pincode"
                  />
                  <p className="text-xs text-muted-foreground">
                    Required for export when buyer address has no Indian pincode.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="transportDistanceKmDomestic">Distance (KM)</Label>
              <Input
                id="transportDistanceKmDomestic"
                value={value.ewayDistanceKm}
                onChange={(e) => patch({ ewayDistanceKm: e.target.value })}
                disabled={!isEditable}
                inputMode="numeric"
                placeholder="e.g. 120"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onConfirm()}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {confirmLabel === 'Save & generate' ? <Truck className="h-4 w-4 mr-2" /> : null}
            {saving ? 'Saving…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
